import { OneOf } from '@everlutionsk/helpers';
import {
  Box,
  Tab as MuiTab,
  TabsProps as MuiTabProps,
  Tabs as MuiTabs,
  SxProps,
  Theme
} from '@mui/material';
import { Properties as CssProperties } from 'csstype';
import React, { ReactNode } from 'react';
import { Divider } from './coreComponents/divider';

export interface TabsTheme {
  readonly indicatorColor: CssProperties['color'];
  readonly activeTextColor: CssProperties['color'];
  readonly inactiveTextColor: CssProperties['color'];
}

export interface TabsProps<T>
  extends Omit<MuiTabProps, 'children' | 'onChange' | 'value' | 'action'> {
  readonly tabs: ReadonlyArray<Tab<T> | false | undefined | null>;
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly action?: ReactNode;
}

export interface Tab<T> {
  readonly value: OneOf<T>;
  readonly label: ReactNode;
  readonly node: ReactNode;
  readonly icon?: ReactNode;
  readonly sx?: SxProps<Theme>;
}

/**
 * Material UI tabs with strongly typed values.
 */
export function Tabs<T>(props: TabsProps<T>) {
  const tabs = props.tabs.filter(isTab);
  const active = tabs.find(tab => tab.value === props.value);

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
          width: '100%'
        }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <MuiTabs
            variant="scrollable"
            scrollButtons="auto"
            sx={props.sx}
            value={props.value}
            onChange={(_, value) => props.onChange(value)}
          >
            {tabs.map((tab, index) => (
              <MuiTab key={index} value={tab.value} label={tab.label} />
            ))}
          </MuiTabs>
        </Box>
        {props.action != null && <Box sx={{ flexShrink: 0 }}>{props.action}</Box>}
      </Box>
      <Divider sx={{ mb: 7, width: '100%' }} />

      {active?.node}
    </div>
  );
}

function isTab<T>(value: T | null | undefined | false): value is T {
  return value != null && value !== false;
}
